import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import {
  chunkGalleryImages,
  classifyPhoto,
  GalleryLightbox,
  GalleryMosaicBlock,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Unified photo gallery — all property photos in a repeating 2×2 + fifth
 * image layout: on mobile the fifth spans full width as a square; on desktop
 * it becomes a tall portrait beside the quad. Side alternates each block.
 *
 * Weidner override: adds Community / Apartment filter toggles above the mosaic
 * so visitors can narrow the gallery to just community or in-unit photos. The
 * `_galleryShared` helper is copied locally because the parent theme does not
 * expose it through its package exports map.
 */

type GalleryFilter = 'all' | 'community' | 'apartment';

const FILTER_OPTIONS: { value: GalleryFilter; label: string }[] = [
  { value: 'all', label: 'All Photos' },
  { value: 'community', label: 'Community' },
  { value: 'apartment', label: 'Apartment' },
];

export function PhotoGallery() {
  const images = useCollectionBinding<GalleryImage>('photogallery.images', {
    collection: 'photo_gallery',
  });

  const [filter, setFilter] = useState<GalleryFilter>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const photoList = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const excludedTitles = new Set(['other', 'floorplan', 'logo']);
    return list.filter(
      (img) =>
        Boolean(img.image) &&
        !excludedTitles.has((img.title || '').toLowerCase().trim()),
    );
  }, [images]);

  // Which category toggles actually have photos to show.
  const availableFilters = useMemo(() => {
    const present = new Set(photoList.map((img) => classifyPhoto(img)));
    return FILTER_OPTIONS.filter(
      (opt) => opt.value === 'all' || present.has(opt.value),
    );
  }, [photoList]);

  const showFilters = availableFilters.length > 1;

  const filteredPhotos = useMemo(() => {
    if (filter === 'all') return photoList;
    return photoList.filter((img) => classifyPhoto(img) === filter);
  }, [photoList, filter]);

  const blocks = useMemo(() => chunkGalleryImages(filteredPhotos), [filteredPhotos]);

  const handleFilterChange = (next: GalleryFilter) => {
    setFilter(next);
    setSelectedIndex(null);
  };

  if (photoList.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
        <GalleryFilterStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained py-8 md:py-12 px-0">
        {showFilters && (
          <div
            className="gallery-filters"
            role="group"
            aria-label="Filter gallery photos"
          >
            {availableFilters.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`gallery-filter${filter === opt.value ? ' gallery-filter--active' : ''}`}
                aria-pressed={filter === opt.value}
                onClick={() => handleFilterChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {filteredPhotos.length === 0 ? (
          <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">No photos in this category.</p>
          </div>
        ) : (
          <div className="gallery-mosaic-stack">
            {blocks.map((block, blockIndex) => (
              <GalleryMosaicBlock
                key={blockIndex}
                images={block}
                startIndex={blockIndex * 5}
                tallOnRight={blockIndex % 2 === 0}
                onSelect={setSelectedIndex}
              />
            ))}
          </div>
        )}
      </div>

      <GalleryLightbox
        images={filteredPhotos}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
      <GalleryFilterStyles />
    </section>
  );
}

/** Styling for the Community / Apartment filter toggle row. */
function GalleryFilterStyles() {
  return (
    <style>{`
      .gallery-filters {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 1.5rem;
        padding: 0 0.5rem;
      }

      @media (min-width: 768px) {
        .gallery-filters {
          padding: 0;
          margin-bottom: 2rem;
        }
      }

      .gallery-filter {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.5rem 1.25rem;
        border: 1px solid var(--vibe-color-border, rgba(0, 0, 0, 0.15));
        border-radius: 999px;
        background: transparent;
        font-family: var(--vibe-font-body);
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0.03em;
        color: var(--vibe-color-text, #1a1a1a);
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
      }

      .gallery-filter:hover {
        border-color: var(--vibe-color-primary, #1a1a1a);
      }

      .gallery-filter--active {
        background: var(--vibe-color-primary, #1a1a1a);
        border-color: var(--vibe-color-primary, #1a1a1a);
        color: var(--vibe-color-primary-foreground, #ffffff);
      }

      .gallery-filter:focus-visible {
        outline: 2px solid var(--vibe-color-primary, #1a1a1a);
        outline-offset: 2px;
      }
    `}</style>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoGallery',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'photogallery.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description: 'All photos displayed in the alternating mosaic gallery layout.',
    },
  },
};

(PhotoGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PhotoGallery;
